import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { OnboardingPage } from "./pages/Onboarding";
import { AdminDashboard } from "./pages/AdminDashboard";
import { DashLayout } from "./pages/DashLayout";
import { OverviewPage } from "./pages/Overview";
import { MembersPage } from "./pages/Members";
import { MemberDetailPage } from "./pages/MemberDetail";
import { PackagesPage } from "./pages/Packages";
import { InvoicesPage } from "./pages/Invoices";
import { InvoiceDetailPage } from "./pages/InvoiceDetail";
import { ReportsPage } from "./pages/Reports";
import { SettingsLayout } from "./pages/settings/Layout";
import { SettingsVerein } from "./pages/settings/Verein";
import { SettingsRechnungen } from "./pages/settings/Rechnungen";
import { SettingsTemplate } from "./pages/settings/Template";
import { SettingsAbo } from "./pages/settings/Abo";
import { useTenantId } from "./lib/auth";
import type { Me } from "./lib/types";

export type RouterContext = {
  getMe: () => Me | null;
};

// Note: `App.tsx` already bounces anonymous users to the OAuth flow
// before the RouterProvider ever mounts, so every guard here can assume
// `me.user` is present. These checks only decide between intra-SPA
// routes (onboarding vs dash vs admin).

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  beforeLoad: ({ context }) => {
    const me = context.getMe();
    if (me && me.tenants.length > 0) throw redirect({ to: "/" });
  },
  component: OnboardingPage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: ({ context }) => {
    const me = context.getMe();
    if (me && !me.is_admin) throw redirect({ to: "/" });
  },
  component: AdminDashboard,
});

const dashLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "dash-layout",
  beforeLoad: ({ context }) => {
    const me = context.getMe();
    // Admins are allowed to reach /dash even without their own tenant, so
    // they can impersonate another one from /dash/admin.
    if (me && me.tenants.length === 0 && !me.is_admin) {
      throw redirect({ to: "/onboarding" });
    }
  },
  component: DashLayout,
});

const dashIndexRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/",
  component: () => <OverviewPage tenantId={useTenantId()} />,
});

const dashMembersRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/members",
  component: () => <MembersPage tenantId={useTenantId()} />,
});

const dashMemberDetailRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/members/$memberId",
  component: function MemberDetailRoute() {
    const { memberId } = dashMemberDetailRoute.useParams();
    return <MemberDetailPage tenantId={useTenantId()} memberId={memberId} />;
  },
});

const dashPackagesRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/packages",
  component: () => <PackagesPage tenantId={useTenantId()} />,
});

// Legacy URL: the running-subscriptions list now lives on the Beiträge page.
const dashSubscriptionsRedirect = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/subscriptions",
  beforeLoad: () => {
    throw redirect({ to: "/packages" });
  },
});

const dashInvoicesRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/invoices",
  component: () => <InvoicesPage tenantId={useTenantId()} />,
});

const dashInvoiceDetailRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/invoices/$invoiceId",
  component: function InvoiceDetailRoute() {
    const { invoiceId } = dashInvoiceDetailRoute.useParams();
    return <InvoiceDetailPage tenantId={useTenantId()} invoiceId={invoiceId} />;
  },
});

const dashReportsRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  path: "/reports",
  component: () => <ReportsPage tenantId={useTenantId()} />,
});

// Settings layout route with four child tabs. "/settings"
// itself just bounces to /dash/settings/verein via beforeLoad on
// the index child.
const settingsLayoutRoute = createRoute({
  getParentRoute: () => dashLayoutRoute,
  id: "settings-layout",
  component: SettingsLayout,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings",
  beforeLoad: () => {
    throw redirect({ to: "/settings/verein" });
  },
});

const settingsVereinRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/verein",
  component: SettingsVerein,
});

const settingsRechnungenRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/rechnungen",
  component: SettingsRechnungen,
});

const settingsTemplateRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/template",
  component: SettingsTemplate,
});

const settingsAboRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: "/settings/abo",
  component: SettingsAbo,
});

const routeTree = rootRoute.addChildren([
  onboardingRoute,
  adminRoute,
  dashLayoutRoute.addChildren([
    dashIndexRoute,
    dashMembersRoute,
    dashMemberDetailRoute,
    dashSubscriptionsRedirect,
    dashPackagesRoute,
    dashInvoicesRoute,
    dashInvoiceDetailRoute,
    dashReportsRoute,
    settingsLayoutRoute.addChildren([
      settingsIndexRoute,
      settingsVereinRoute,
      settingsRechnungenRoute,
      settingsTemplateRoute,
      settingsAboRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  context: {
    getMe: () => null,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
